import { useState, useCallback } from 'react';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useFloating, offset, flip, shift, autoUpdate, FloatingPortal } from '@floating-ui/react';
import { ResizeHandle, DependencyCountBadge, SourceIdBadge } from '@/components/atoms';
import { TIMELINE_LAYOUT, LABEL_CONFIG } from '@/constants/timeline';
import { isDueBeforeStart } from '@/lib/utils';
import type { GroupedData, DeliverableTimelineData, GroupMode } from '@/types/timeline';

export interface TimelineLabelColumnProps {
  /** Width of the label column in pixels */
  labelWidth: number;
  /** Callback when label column is resized */
  onLabelWidthChange: (width: number) => void;
  /** Grouped timeline data */
  groupedData: GroupedData[];
  /** Set of collapsed group names */
  collapsedSet: Set<string>;
  /** Callback to toggle a group's expanded/collapsed state */
  onToggleGroup: (groupName: string) => void;
  /** Current group mode (team or feature) */
  groupMode: GroupMode;
  /** Get team color by name */
  getTeamColor: (teamName: string) => string;
  /** Callback when a deliverable label is clicked */
  onDeliverableClick?: (data: DeliverableTimelineData) => void;
  /** Top padding to align with chart body (for due date badges) */
  topPadding?: number;
  /** Simulation start date; due dates before this predate the forecast and are shown as warnings (S3) */
  simulationStartDate?: Date;
  /** ID of the deliverable whose dependency popover is currently open */
  activeDependencyId?: string | null;
  /** Callback when a dependency badge is clicked */
  onDependencyClick?: (event: React.MouseEvent<HTMLButtonElement>, deliverableId: string) => void;
  /** Callback when a row is clicked to show/hide dependency connections */
  onRowConnectionClick?: (deliverableId: string | null) => void;
  /** ID of the deliverable whose connections are currently shown */
  selectedConnectionId?: string | null;
  /** Function to check if a row should be dimmed (not in active dependency path) */
  shouldDimRow?: (deliverableId: string | null) => boolean;
  /** Function to check if a row should be hidden (hide mode + not in path) */
  shouldHideRow?: (deliverableId: string | null) => boolean;
  /** Function to get the path highlight color for a deliverable */
  getPathHighlightColor?: (deliverableId: string | null) => string | null;
  /** Whether to hide group headers (in hide mode with active path) */
  hideGroupHeaders?: boolean;
}

interface OverdueWarningProps {
  dueDate: Date;
  deliverableName: string;
}

/**
 * Overdue warning icon with floating tooltip
 */
function OverdueWarning({ dueDate, deliverableName }: OverdueWarningProps) {
  const [isHovered, setIsHovered] = useState(false);

  const { refs, floatingStyles } = useFloating({
    open: isHovered,
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  // Destructure the stable callback-ref setters so JSX references plain functions
  // rather than reading properties off the `refs` object during render.
  const { setReference, setFloating } = refs;

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <>
      <div
        ref={setReference}
        className="mr-2 shrink-0 cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <AlertTriangle className="size-4 text-feedback-error" />
      </div>

      {isHovered && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={floatingStyles}
            className="
              pointer-events-none z-50 rounded-lg border border-border-default bg-bg-elevated px-3 py-2 text-sm
              text-text-primary shadow-lg
            "
          >
            <div className="font-semibold text-feedback-error">Due before forecast start</div>
            <div className="mt-1 text-text-disabled">
              Due {format(dueDate, 'MMM d, yyyy')} — before the simulation begins
            </div>
            <div className="mt-1 border-t border-border-default pt-1 text-xs text-text-muted">{deliverableName}</div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

interface LabelRowProps {
  mainLabel: string;
  subLabel?: string;
  /** External issue ID (e.g. "PROJ-123"); shown as a compact badge before the label */
  sourceId?: string | null;
  color: string;
  onClick?: () => void;
  isGroupHeader?: boolean;
  itemCount?: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  showFeatureBorder?: boolean;
  groupColor?: string;
  /** Due date that's before timeline start (shows warning icon) */
  overdueDueDate?: Date;
  /** Number of incoming dependencies (predecessors) */
  incomingCount?: number;
  /** Number of outgoing dependencies (dependents) */
  outgoingCount?: number;
  /** Whether the dependency badge is in active state */
  isDependencyActive?: boolean;
  /** Callback when dependency badge is clicked */
  onDependencyClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Deliverable ID for navigation targeting */
  deliverableId?: string | null;
  /** Callback when row is clicked to show/hide dependency connections */
  onRowConnectionClick?: (deliverableId: string | null) => void;
  /** Whether this row's connections are currently shown */
  isConnectionSelected?: boolean;
  /** Whether this row should be dimmed (not in active dependency path) */
  isDimmed?: boolean;
  /** Path highlight color (when this row is in the dependency path) */
  pathHighlightColor?: string | null;
  /** Whether this is the SELECTED deliverable (the one clicked to show dependencies) */
  isSelected?: boolean;
}

/**
 * Single label row - either a deliverable or group header
 */
function LabelRow({
  mainLabel,
  subLabel,
  sourceId,
  color,
  onClick,
  isGroupHeader,
  itemCount,
  isExpanded,
  onToggle,
  showFeatureBorder,
  groupColor,
  overdueDueDate,
  incomingCount = 0,
  outgoingCount = 0,
  isDependencyActive,
  onDependencyClick,
  deliverableId,
  onRowConnectionClick,
  isConnectionSelected,
  isDimmed,
  pathHighlightColor,
  isSelected,
}: LabelRowProps) {
  const rowHeight = TIMELINE_LAYOUT.ROW_HEIGHT;

  if (isGroupHeader) {
    return (
      <div
        className="relative flex cursor-pointer items-center select-none"
        style={{
          height: rowHeight,
          backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
        }}
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
      >
        {/* Left accent bar */}
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: TIMELINE_LAYOUT.GROUP_ACCENT_WIDTH,
            backgroundColor: color,
            opacity: TIMELINE_LAYOUT.GROUP_HEADER_ACCENT_OPACITY,
          }}
        />

        {/* Chevron */}
        <div className="ml-1.5 flex w-6 items-center justify-center">
          <ChevronRight
            className={`
              size-4 text-text-primary transition-transform duration-200
              ${isExpanded ? `rotate-90` : ''}
            `}
          />
        </div>

        {/* Label content */}
        <div className="min-w-0 flex-1 pr-2">
          <div className="truncate text-xs font-semibold text-text-primary" title={mainLabel}>
            {mainLabel}
          </div>
          <div className="text-[10px] text-text-muted">
            {itemCount} deliverable{itemCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    );
  }

  // Deliverable row
  const hasDependencies = incomingCount > 0 || outgoingCount > 0;

  // Combined click handler: show connections (if available) AND trigger onClick
  const handleRowClick = () => {
    // Toggle connection lines if this row has dependencies
    if (onRowConnectionClick && deliverableId && hasDependencies) {
      onRowConnectionClick(deliverableId);
    }
    // Also trigger the standard onClick (e.g., open details panel)
    onClick?.();
  };

  // Calculate background color based on state
  // Connection selected: highlighted (25%), Dimmed: faded (3%), Normal: default (10%)
  const bgMixPercent = isConnectionSelected ? 25 : isDimmed ? 3 : 10;
  // Use path highlight color for connection targets, otherwise use team color
  const rowBackgroundColor = isConnectionSelected && pathHighlightColor ? pathHighlightColor : color;

  // Border style for selected item
  const selectedBorderStyle =
    isSelected && pathHighlightColor ? { boxShadow: `inset 0 0 0 2px ${pathHighlightColor}`, borderRadius: '4px' } : {};

  return (
    <div
      className={`
        relative flex items-center transition-all duration-200
        ${
          onClick || (hasDependencies && onRowConnectionClick)
            ? `
              cursor-pointer
              hover:bg-action-secondary-hover/10
            `
            : ''
        }
      `}
      style={{
        height: rowHeight,
        backgroundColor: `color-mix(in srgb, ${rowBackgroundColor} ${bgMixPercent}%, transparent)`,
        opacity: isDimmed ? 0.4 : 1,
        ...selectedBorderStyle,
      }}
      onClick={handleRowClick}
      data-deliverable-id={deliverableId ?? undefined}
    >
      {/* Left accent bar - connects items visually to their group */}
      {showFeatureBorder && groupColor && (
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: TIMELINE_LAYOUT.GROUP_ACCENT_WIDTH,
            backgroundColor: groupColor,
            opacity: TIMELINE_LAYOUT.GROUP_ACCENT_OPACITY,
          }}
        />
      )}

      {/* Label content */}
      <div className="min-w-0 flex-1 px-3.5">
        <div className="flex items-center gap-1.5">
          {sourceId && <SourceIdBadge sourceId={sourceId} variant="compact" size="sm" className="shrink-0" />}
          <span
            className={`
              truncate text-xs font-medium text-text-primary
              ${onClick ? `hover:underline` : ''}
            `}
            title={mainLabel}
          >
            {mainLabel}
          </span>
        </div>
        {subLabel && (
          <div className="truncate text-[10px] text-text-muted" title={subLabel}>
            {subLabel}
          </div>
        )}
      </div>

      {/* Dependency count badge */}
      {hasDependencies && onDependencyClick && (
        <DependencyCountBadge
          incomingCount={incomingCount}
          outgoingCount={outgoingCount}
          isActive={isDependencyActive}
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            onDependencyClick(e);
          }}
          className="mr-2 shrink-0"
        />
      )}

      {/* Overdue warning icon with floating tooltip */}
      {overdueDueDate && <OverdueWarning dueDate={overdueDueDate} deliverableName={mainLabel} />}
    </div>
  );
}

/**
 * TimelineLabelColumn molecule - HTML-based label column with sticky positioning
 *
 * Extracts labels from SVG into a separate HTML column that can stick to the left
 * edge during horizontal scroll. Includes a resize handle for adjusting width.
 *
 * Key architectural benefits over SVG labels:
 * - Native CSS `position: sticky` for left-edge pinning during horizontal scroll
 * - CSS `text-overflow: ellipsis` for automatic text truncation
 * - Better accessibility with native HTML elements
 * - Simple resize handle implementation
 */
export function TimelineLabelColumn({
  labelWidth,
  onLabelWidthChange,
  groupedData,
  collapsedSet,
  onToggleGroup,
  groupMode,
  getTeamColor,
  onDeliverableClick,
  topPadding = TIMELINE_LAYOUT.CHART_TOP_PADDING,
  simulationStartDate,
  activeDependencyId,
  onDependencyClick,
  onRowConnectionClick,
  selectedConnectionId,
  shouldDimRow,
  shouldHideRow,
  getPathHighlightColor,
  hideGroupHeaders,
}: TimelineLabelColumnProps) {
  // Helper to check if a due date predates the simulation start (deadline that
  // already passed before the forecast could begin — impossible to meet).
  const getOverdueDueDate = (data: DeliverableTimelineData): Date | undefined => {
    if (!simulationStartDate) return undefined;
    return isDueBeforeStart(data.dueDate, simulationStartDate) ? (data.dueDate ?? undefined) : undefined;
  };
  const handleResize = useCallback(
    (delta: number) => {
      const newWidth = Math.max(LABEL_CONFIG.MIN_WIDTH, Math.min(LABEL_CONFIG.MAX_WIDTH, labelWidth + delta));
      onLabelWidthChange(newWidth);
    },
    [labelWidth, onLabelWidthChange],
  );

  const handleResizeEnd = useCallback(() => {
    // Snap to nearest 10px for cleaner values
    const snappedWidth = Math.round(labelWidth / 10) * 10;
    const clampedWidth = Math.max(LABEL_CONFIG.MIN_WIDTH, Math.min(LABEL_CONFIG.MAX_WIDTH, snappedWidth));
    if (clampedWidth !== labelWidth) {
      onLabelWidthChange(clampedWidth);
    }
  }, [labelWidth, onLabelWidthChange]);

  return (
    <div className="relative bg-bg-base" style={{ width: labelWidth }}>
      {/* Resize handle */}
      <ResizeHandle onResize={handleResize} onResizeEnd={handleResizeEnd} />

      {/* Top padding spacer to align with chart body */}
      <div style={{ height: topPadding }} />

      {/* Label rows */}
      {groupedData.map((group, groupIndex) => {
        const groupColor = groupMode === 'team' ? getTeamColor(group.groupName) : '#6b7280';
        const isExpanded = !collapsedSet.has(group.groupName);
        const visibleItems = group.items.filter((data) => !shouldHideRow?.(data.deliverableId));

        // Skip entirely empty groups in hide mode
        if (hideGroupHeaders && visibleItems.length === 0) {
          return null;
        }

        // Track if this is the first visible group (for separator logic)
        const isFirstVisibleGroup =
          groupIndex === 0 ||
          groupedData
            .slice(0, groupIndex)
            .every((g) => hideGroupHeaders && g.items.every((d) => shouldHideRow?.(d.deliverableId)));

        return (
          <div key={`group-${groupIndex}`}>
            {/* Group separator (not before first visible group, not when headers hidden) */}
            {!isFirstVisibleGroup && !hideGroupHeaders && (
              <div
                className="border-y border-border-muted/30 bg-bg-subtle"
                style={{ height: TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT }}
              />
            )}

            {/* Group header (hidden when hideGroupHeaders is true) */}
            {!hideGroupHeaders && (
              <LabelRow
                mainLabel={group.groupName}
                color={groupColor}
                isGroupHeader
                itemCount={group.items.length}
                isExpanded={isExpanded}
                onToggle={() => onToggleGroup(group.groupName)}
              />
            )}

            {/* Item rows (only if expanded, no header, or headers are hidden) */}
            {(isExpanded || hideGroupHeaders) &&
              group.items.map((data) => {
                // Skip hidden rows in hide mode
                if (shouldHideRow?.(data.deliverableId)) {
                  return null;
                }

                const itemColor = groupMode === 'team' ? groupColor : getTeamColor(data.teamName);
                // Sub-label content depends on group mode:
                // - Team view: show feature name (if different from deliverable name)
                // - Feature view: show "TeamName (#priority)" to indicate team position
                const subLabel =
                  groupMode === 'team'
                    ? data.featureName && data.featureName !== data.deliverableName
                      ? data.featureName
                      : undefined
                    : `${data.teamName} (#${data.teamPriority})`;

                return (
                  <LabelRow
                    key={data.id}
                    mainLabel={data.deliverableName}
                    subLabel={subLabel}
                    sourceId={data.sourceId}
                    color={itemColor}
                    onClick={onDeliverableClick ? () => onDeliverableClick(data) : undefined}
                    showFeatureBorder
                    groupColor={itemColor}
                    overdueDueDate={getOverdueDueDate(data)}
                    incomingCount={data.predecessorIds.length}
                    outgoingCount={data.dependentIds.length}
                    isDependencyActive={data.deliverableId === activeDependencyId}
                    onDependencyClick={
                      data.deliverableId && onDependencyClick
                        ? (e) => onDependencyClick(e, data.deliverableId!)
                        : undefined
                    }
                    deliverableId={data.deliverableId}
                    onRowConnectionClick={onRowConnectionClick}
                    isConnectionSelected={data.deliverableId === selectedConnectionId}
                    isDimmed={shouldDimRow?.(data.deliverableId) ?? false}
                    pathHighlightColor={getPathHighlightColor?.(data.deliverableId) ?? null}
                    isSelected={data.deliverableId === selectedConnectionId}
                  />
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
